import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, ViewMode } from '../types';
import {
  hasSystemPermission,
  hasProjectPermission,
  hasTeamPermission,
  isAdmin,
  type SystemPermission,
  type ProjectPermission,
  type TeamPermission,
  type SystemRole,
  type ProjectRole,
  type TeamRole,
} from '../utils/permissions';

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  setAuth: (user: User, token: string) => void;
  clearAuth: () => void;
  // Permission helpers
  can: (permission: SystemPermission) => boolean;
  canInProject: (permission: ProjectPermission, projectRole?: ProjectRole) => boolean;
  canInTeam: (permission: TeamPermission, teamRole?: TeamRole) => boolean;
  isAdmin: () => boolean;
}

interface AppState {
  // Sidebar
  sidebarOpen: boolean;
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
  toggleSidebarCollapsed: () => void;
  closeSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;

  // Current view mode
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;

  // Selected items
  selectedProjectId: string | null;
  selectedTaskId: string | null;
  setSelectedProject: (id: string | null) => void;
  setSelectedTask: (id: string | null) => void;

  // Task modal
  taskModalOpen: boolean;
  taskModalTaskId: string | null;
  openTaskModal: (taskId?: string | null) => void;
  closeTaskModal: () => void;

  // Quick task creation
  quickTaskProjectId: string | null;
  quickTaskSectionId: string | null;
  setQuickTask: (projectId: string | null, sectionId?: string | null) => void;

  // Global search
  searchOpen: boolean;
  openSearch: () => void;
  closeSearch: () => void;

  // Shared notification unread count (written by Header, read by MobileBottomNav)
  notificationUnreadCount: number;
  setNotificationUnreadCount: (count: number) => void;

  // Native push token, forwarded during logout so the server can detach this device atomically.
  pushDeviceToken: string | null;
  setPushDeviceToken: (token: string | null) => void;
}

const PUSH_DEVICE_TOKEN_KEY = 'pm-push-device-token';
const LEGACY_PUSH_DEVICE_TOKEN_KEY = 'hr-push-device-token';
const AUTH_STORAGE_KEY = 'pm-auth-storage';
const LEGACY_AUTH_STORAGE_KEY = 'hr-auth-storage';

function readStoredPushDeviceToken(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const current = localStorage.getItem(PUSH_DEVICE_TOKEN_KEY);
    if (current) return current;
    const legacy = localStorage.getItem(LEGACY_PUSH_DEVICE_TOKEN_KEY);
    if (legacy) {
      localStorage.setItem(PUSH_DEVICE_TOKEN_KEY, legacy);
      localStorage.removeItem(LEGACY_PUSH_DEVICE_TOKEN_KEY);
    }
    return legacy;
  } catch {
    return null;
  }
}

function persistPushDeviceToken(token: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (token) localStorage.setItem(PUSH_DEVICE_TOKEN_KEY, token);
    else localStorage.removeItem(PUSH_DEVICE_TOKEN_KEY);
    localStorage.removeItem(LEGACY_PUSH_DEVICE_TOKEN_KEY);
  } catch {
    // Storage can be unavailable in privacy-restricted browser contexts.
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      setAuth: (user, token) => set({ user, token, isAuthenticated: true }),
      clearAuth: () => set({ user: null, token: null, isAuthenticated: false }),
      // Permission helpers
      can: (permission: SystemPermission) => {
        const { user } = get();
        return hasSystemPermission(user?.role as SystemRole, permission);
      },
      canInProject: (permission: ProjectPermission, projectRole?: ProjectRole) => {
        const { user } = get();
        // Admins have all project permissions
        if (user?.role === 'admin') return true;
        return hasProjectPermission(projectRole, permission);
      },
      canInTeam: (permission: TeamPermission, teamRole?: TeamRole) => {
        const { user } = get();
        // Admins have all team permissions
        if (user?.role === 'admin') return true;
        return hasTeamPermission(teamRole, permission);
      },
      isAdmin: () => {
        const { user } = get();
        return isAdmin(user?.role as SystemRole);
      },
    }),
    {
      name: AUTH_STORAGE_KEY,
      storage: {
        getItem: (name) => {
          let value = sessionStorage.getItem(name);
          if (!value && name === AUTH_STORAGE_KEY) {
            value = sessionStorage.getItem(LEGACY_AUTH_STORAGE_KEY);
            if (value) sessionStorage.setItem(AUTH_STORAGE_KEY, value);
            sessionStorage.removeItem(LEGACY_AUTH_STORAGE_KEY);
          }
          return value ? JSON.parse(value) : null;
        },
        setItem: (name, value) => {
          sessionStorage.setItem(name, JSON.stringify(value));
        },
        removeItem: (name) => {
          sessionStorage.removeItem(name);
        },
      },
    },
  ),
);

export const useAppStore = create<AppState>((set) => ({
  sidebarOpen: typeof window !== 'undefined' ? window.innerWidth >= 1024 : true,
  sidebarCollapsed: false,
  toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
  toggleSidebarCollapsed: () =>
    set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed, sidebarOpen: true })),
  closeSidebar: () => set({ sidebarOpen: false }),
  setSidebarOpen: (open) => set({ sidebarOpen: open }),

  viewMode: 'list',
  setViewMode: (mode) =>
    set({ viewMode: ['list', 'board', 'grid', 'analytics'].includes(mode) ? mode : 'list' }),

  selectedProjectId: null,
  selectedTaskId: null,
  setSelectedProject: (id) => set({ selectedProjectId: id }),
  setSelectedTask: (id) => set({ selectedTaskId: id }),

  taskModalOpen: false,
  taskModalTaskId: null,
  openTaskModal: (taskId = null) => set({ taskModalOpen: true, taskModalTaskId: taskId }),
  closeTaskModal: () => set({ taskModalOpen: false, taskModalTaskId: null }),

  quickTaskProjectId: null,
  quickTaskSectionId: null,
  setQuickTask: (projectId, sectionId = null) =>
    set({ quickTaskProjectId: projectId, quickTaskSectionId: sectionId }),

  searchOpen: false,
  openSearch: () => set({ searchOpen: true }),
  closeSearch: () => set({ searchOpen: false }),

  notificationUnreadCount: 0,
  setNotificationUnreadCount: (count) => set({ notificationUnreadCount: count }),

  pushDeviceToken: readStoredPushDeviceToken(),
  setPushDeviceToken: (token) => {
    persistPushDeviceToken(token);
    set({ pushDeviceToken: token });
  },
}));
